import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Types
export type PageContextType = 'general' | 'workflow' | 'agent' | 'conversation' | 'dataset' | 'function' | 'custom-extensions' | 'app_studio';

export interface PageContext {
  type: PageContextType;
  entityId?: string;
  entityName?: string;
  route: string;
}

export interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

interface AICopilotContextValue {
  // Sidebar state
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;

  // Context detection
  pageContext: PageContext;

  // Chat state
  messages: CopilotMessage[];
  isLoading: boolean;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
}

const AICopilotContext = createContext<AICopilotContextValue | undefined>(undefined);

// Context detection from route — exported for testing
export const getPageContext = (pathname: string): PageContext => {
  // AI Workflows
  if (pathname.includes('/ai-workflows')) {
    const match = pathname.match(/\/ai-workflows\/([^/]+)/);
    return {
      type: 'workflow',
      entityId: match?.[1],
      route: pathname,
    };
  }

  // AI Agents
  if (pathname.includes('/ai-agents')) {
    const match = pathname.match(/\/ai-agents\/([^/]+)/);
    return {
      type: 'agent',
      entityId: match?.[1],
      route: pathname,
    };
  }

  // Conversations
  if (pathname.includes('/conversations')) {
    const match = pathname.match(/\/conversations?\/([^/]+)/);
    return {
      type: 'conversation',
      entityId: match?.[1],
      route: pathname,
    };
  }

  // Custom Extensions (datasets + functions)
  if (pathname.includes('/custom-extensions')) {
    return {
      type: 'custom-extensions',
      route: pathname,
    };
  }

  // Custom Datasets (legacy route)
  if (pathname.includes('/custom-datasets')) {
    const match = pathname.match(/\/custom-datasets\/([^/]+)/);
    return {
      type: 'dataset',
      entityId: match?.[1],
      route: pathname,
    };
  }

  // Custom Functions (legacy route)
  if (pathname.includes('/custom-functions')) {
    const match = pathname.match(/\/custom-functions\/([^/]+)/);
    return {
      type: 'function',
      entityId: match?.[1],
      route: pathname,
    };
  }

  // App Studio (Phase 12 UDL · PR-1.12A Step A10)
  //   /tenant-admin/app-studio                          → app_list
  //   /tenant-admin/app-studio/:appSlug                 → app_detail (entityId=appSlug)
  //   /tenant-admin/app-studio/:appSlug/pages/:pageSlug → page_editor (entityId=appSlug:pageSlug)
  if (pathname.includes('/tenant-admin/app-studio')) {
    const pageMatch = pathname.match(/\/tenant-admin\/app-studio\/([^/]+)\/pages\/([^/]+)/);
    if (pageMatch) {
      return {
        type: 'app_studio',
        entityId: `${pageMatch[1]}:${pageMatch[2]}`,
        route: pathname,
      };
    }
    const appMatch = pathname.match(/\/tenant-admin\/app-studio\/([^/]+)/);
    if (appMatch) {
      return {
        type: 'app_studio',
        entityId: appMatch[1],
        route: pathname,
      };
    }
    return { type: 'app_studio', route: pathname };
  }

  return { type: 'general', route: pathname };
};

// Load entity name for context display
const loadEntityName = async (
  type: PageContextType,
  entityId: string,
  tenantSchema?: string
): Promise<string> => {
  if (!entityId || !tenantSchema) return '';

  try {
    switch (type) {
      case 'workflow': {
        const { data } = await supabase.rpc('get_tenant_workflows', {
          tenant_schema: tenantSchema,
          active_only: false,
          limit_count: 100
        });
        const workflow = data?.find((w: { id: string }) => w.id === entityId);
        return workflow?.name || 'Unknown Workflow';
      }
      case 'agent': {
        const { data } = await supabase.rpc('get_tenant_ai_agents_v2', {
          tenant_schema: tenantSchema,
          p_tenant_id: null
        });
        const agent = data?.find((a: { id: string }) => a.id === entityId);
        return agent?.agent_name || 'Unknown Agent';
      }
      case 'dataset': {
        const { data } = await supabase.rpc('get_custom_dataset', {
          p_tenant_schema: tenantSchema,
          p_slug: entityId
        });
        return data?.name || 'Unknown Dataset';
      }
      case 'function': {
        const { data } = await supabase.rpc('get_custom_function', {
          p_tenant_schema: tenantSchema,
          p_slug: entityId
        });
        return data?.name || 'Unknown Function';
      }
      default:
        return '';
    }
  } catch (error) {
    console.error('Error loading entity name:', error);
    return '';
  }
};

export const AICopilotProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pageContext, setPageContext] = useState<PageContext>({ type: 'general', route: '/' });
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const { user } = useAuth();
  const tenantSchema = user?.user_metadata?.schema_name;

  // Toggle sidebar
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Update context on route change
  useEffect(() => {
    const context = getPageContext(location.pathname);

    if (context.entityId && tenantSchema) {
      loadEntityName(context.type, context.entityId, tenantSchema)
        .then(name => {
          setPageContext({ ...context, entityName: name });
        });
    } else {
      setPageContext(context);
    }
  }, [location.pathname, tenantSchema]);

  // Keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
      // Close on Escape
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggle, close, isOpen]);

  // Map page context type to component type for ai-config-assistant
  const getComponentType = (pageType: PageContextType): string => {
    switch (pageType) {
      case 'workflow': return 'workflow';
      case 'agent': return 'ai_node';
      case 'dataset': return 'generic';
      case 'function': return 'generic';
      case 'custom-extensions': return 'generic';
      case 'conversation': return 'generic';
      case 'app_studio': return 'app_studio';
      default: return 'generic';
    }
  };

  // Send message to AI
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: CopilotMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Call ai-config-assistant Edge Function
      const { data, error } = await supabase.functions.invoke('ai-config-assistant', {
        body: {
          // Required fields for ai-config-assistant
          action: 'suggest',
          message: content.trim(),
          componentType: getComponentType(pageContext.type),
          componentId: pageContext.entityId || 'copilot-global',
          tenantId: user?.user_metadata?.tenant_id,
          userId: user?.id,
          userLocale: 'es-CL',
          workflowId: pageContext.type === 'workflow' ? pageContext.entityId : undefined,
          workflowName: pageContext.entityName || 'Platform Copilot',
          currentConfig: {},
          // Context for AI understanding
          context: {
            pageType: pageContext.type,
            entityId: pageContext.entityId,
            entityName: pageContext.entityName,
            route: pageContext.route,
          },
          conversationHistory: messages.map(m => ({
            role: m.role,
            content: m.content,
          })),
        },
      });

      if (error) throw error;

      const assistantMessage: CopilotMessage = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: data?.content || data?.response || 'I apologize, I was unable to generate a response.',
        timestamp: new Date().toISOString(),
        metadata: data?.metadata,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message to copilot:', error);

      const errorMessage: CopilotMessage = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: 'I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [pageContext, messages, isLoading, user]);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const contextValue: AICopilotContextValue = {
    isOpen,
    toggle,
    open,
    close,
    pageContext,
    messages,
    isLoading,
    sendMessage,
    clearMessages,
  };

  return (
    <AICopilotContext.Provider value={contextValue}>
      {children}
    </AICopilotContext.Provider>
  );
};

export const useAICopilot = () => {
  const context = useContext(AICopilotContext);
  if (context === undefined) {
    throw new Error('useAICopilot must be used within an AICopilotProvider');
  }
  return context;
};
